import React, { useEffect } from 'react';

import { Divider, Flex, Grid, GridItem } from '@chakra-ui/react';
import styled from '@emotion/styled';

import { ReviewsIcon } from 'assets/assetsV1/iconComponents';
import NoDataImg from 'assets/assetsV1/illustrations/homepage/noReviewSummaryCard.svg';

import { Avatar } from 'uiToolkitV1/Avatar';
import { DateComp } from 'uiToolkitV1/DateComp';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { EmptySummaryCard } from '../../EmptyCard/EmptySummaryCard';
import { useSummaryPagination } from '../../hooks/useSummaryPagination';
import { ProfileSummaryCardTemplate } from '../ProfileSummaryCardTemplate';
import { ReviewActionButton } from './ReviewActionButton';
import { useMySummaryReviews } from './hooks';

export const PROVIDER_TYPE_TITLE_MAP = {
  SELF: 'Self',
  MANAGER: 'Manager',
  PEER: 'Peer',
  UPWARD: 'Upward',
  NOMINATOR: 'Peer Nomination',
};

const Container = styled(Flex)`
  padding: 12px 8px;
  &:hover {
    background: ${(props) => props.theme.colors.neutral.F6FAFE};
    & .highlightTextOnHover {
      color: #1565bc;
    }
  }
`;

const ButtonContainer = styled(Flex)`
  height: 100%;
  visibility: hidden;
  align-items: center;
  & .action {
    margin-left: auto;
  }
  ${Container}:hover & {
    visibility: visible;
  }
`;

export const ReviewsMySummaryRow = ({ employee, deadline, review, showDivider = true, nominationsCount }) => {
  const { displayName, profilePhoto } = employee ?? {};
  const { providerType } = review ?? {};
  const reviewType = PROVIDER_TYPE_TITLE_MAP[providerType];
  const reviewDeadline = deadline ?? Date.now();
  return (
    <>
      <Container flexDir="column">
        <Grid templateColumns="repeat(6, 1fr)" width="100%" gap={2}>
          <GridItem colSpan={4}>
            <TextLabel color="text.g2" mb="4px">
              Review for
            </TextLabel>
            <Flex alignItems="center" sx={{ marginBottom: '8px' }}>
              <Avatar size={24} src={profilePhoto} name={displayName} />
              <TextBodyMedium color="text.g1" ml="8px" className="highlightTextOnHover">
                {displayName}
              </TextBodyMedium>
            </Flex>
            <Flex>
              <TextBodyMedium color="text.g2">
                Review Type: {reviewType}
                {deadline && <>&nbsp;• &nbsp;</>}
              </TextBodyMedium>
              {deadline ? (
                <Flex>
                  <TextBodyMedium color="text.g2">
                    Due:<>&nbsp;</>
                  </TextBodyMedium>
                  <DateComp
                    date={reviewDeadline}
                    isRelativeDateFormat
                    enableRelativeFormatDistanceColor
                    // textComponent={TextBodyMedium}
                    forceRelativeDateFormat
                    showDateInBrackets
                  />
                </Flex>
              ) : null}
            </Flex>
          </GridItem>
          <GridItem colStart={5} colEnd={7}>
            <ButtonContainer className="showOnHover">
              <ReviewActionButton review={review} nominationsCount={nominationsCount} />
            </ButtonContainer>
          </GridItem>
        </Grid>
      </Container>
      <Divider color="neutral.CFD7DF" />
    </>
  );
};

const ReviewsProfileSummaryCard = ({ employeeId }) => {
  const { hasMore, size, setDataLength, setPage } = useSummaryPagination();

  const { data, isLoading, isPreviousData, isFetching, isError } = useMySummaryReviews({
    limit: size,
    offset: 0,
    sectionId: 'MY_PAST_REVIEWS',
    employeeId,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!isLoading) {
      setDataLength(data?.data?.length);
    }
  }, [isLoading, data]);

  const reviewsExist = Boolean(data?.data?.length);

  if (isLoading) {
    return (
      <ProfileSummaryCardTemplate showViewAllButton={false} Icon={ReviewsIcon} heading="Reviews">
        <div style={{ marginTop: '16px' }}>
          <SkeletonLoader />
        </div>
      </ProfileSummaryCardTemplate>
    );
  }
  if (!reviewsExist) {
    return null;
  }
  if (isError) {
    return null;
  }
  if (!data?.data?.length) {
    return (
      <EmptySummaryCard
        Icon={ReviewsIcon}
        heading="Reviews"
        description="There are no recent reviews to see"
        illustrationSrc={NoDataImg}
      />
    );
  }

  return (
    <ProfileSummaryCardTemplate
      Icon={ReviewsIcon}
      heading="Reviews"
      showViewAllButton={hasMore}
      onClickViewAll={() => {
        setPage((prev) => prev + 1);
      }}
      viewAllBtnDisabled={isPreviousData || !hasMore}
    >
      {data?.data?.map(({ employee, deadline, review, nominationsCount }, index) => (
        <ReviewsMySummaryRow
          employee={employee}
          deadline={deadline}
          review={review}
          key={`${employee?.displayName}-${index}-review-row`}
          showDivider={data?.data?.length - 1 !== index}
          nominationsCount={nominationsCount}
        />
      ))}
    </ProfileSummaryCardTemplate>
  );
};

export { ReviewsProfileSummaryCard };
